import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import type {
  AppointmentSlotDetail,
  AppointmentSlotTimeDto,
  AppointmentSlotsResponse,
  GetAppointmentSlotsParams,
  GetUpcomingAppointmentParams,
  ScheduleAppointmentRequest,
  ScheduleAppointmentResponse,
  UpcomingAppointmentDetail,
  UpcomingAppointmentDto,
  UpcomingAppointmentResponse,
  UpdateAppointmentRequest,
  UpdateAppointmentResponse,
} from '@/core/api/models/appointments';
import { logger } from '@/core/logger';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/** Format slot start_time for chips, e.g. "9:00 AM" (UAE). */
function formatSlotStartLabel(isoStart: string): string {
  const date = new Date(isoStart);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Dubai',
  });
}

function extractSlotDetails(
  response: AppointmentSlotsResponse | null | undefined,
): AppointmentSlotDetail[] {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.details)) {
    return response.details;
  }
  const data = asRecord(response.data);
  if (data && Array.isArray(data.details)) {
    return data.details as AppointmentSlotDetail[];
  }
  return [];
}

function extractLegacySlotTimeDtos(
  response: AppointmentSlotsResponse | null | undefined,
): AppointmentSlotTimeDto[] {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.times)) {
    return response.times;
  }
  if (Array.isArray(response.slots)) {
    return response.slots;
  }
  const data = asRecord(response.data);
  if (data) {
    if (Array.isArray(data.times)) {
      return data.times as AppointmentSlotTimeDto[];
    }
    if (Array.isArray(data.slots)) {
      return data.slots as AppointmentSlotTimeDto[];
    }
  }
  if (Array.isArray(response.data)) {
    return response.data as AppointmentSlotTimeDto[];
  }
  return [];
}

type MappedSlotTime = {
  id: string;
  label: string;
  value: string;
  disabled?: boolean;
};

/** Minutes from midnight for sorting chip labels like "9:00 AM". */
function parseDisplayTimeLabelToMinutes(label: string): number | null {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(label.trim());
  if (!match) {
    return null;
  }
  let hours = Number.parseInt(match[1] ?? '', 10);
  const minutes = Number.parseInt(match[2] ?? '', 10);
  const meridiem = (match[3] ?? '').toUpperCase();
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  if (meridiem === 'PM' && hours !== 12) {
    hours += 12;
  }
  if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }
  return hours * 60 + minutes;
}

function mappedSlotSortKey(slot: MappedSlotTime): number {
  const fromValue = Date.parse(slot.value);
  if (!Number.isNaN(fromValue)) {
    return fromValue;
  }
  const fromLabel = parseDisplayTimeLabelToMinutes(slot.label);
  if (fromLabel != null) {
    return fromLabel;
  }
  return Number.MAX_SAFE_INTEGER;
}

function compareMappedSlotTimes(a: MappedSlotTime, b: MappedSlotTime): number {
  const diff = mappedSlotSortKey(a) - mappedSlotSortKey(b);
  if (diff !== 0) {
    return diff;
  }
  return a.label.localeCompare(b.label);
}

/**
 * Slot status from GET slots: "Available" | "Scheduled".
 * Only Available is selectable; Scheduled (and any other non-empty status) is disabled.
 */
function isSlotStatusUnavailable(status: unknown): boolean {
  const normalized = toTrimmedString(status).toLowerCase();
  if (!normalized) {
    return false;
  }
  return normalized !== 'available';
}

function toTrimmedString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}`;
  }
  return '';
}

function firstNonEmptyString(parts: unknown[]): string {
  for (const part of parts) {
    const text = toTrimmedString(part);
    if (text.length > 0) {
      return text;
    }
  }
  return '';
}

function resolveLegacySlotId(
  dto: AppointmentSlotTimeDto,
  label: string,
  index: number,
): string {
  const candidate = dto.appointment_slot_id ?? dto.id ?? label ?? `slot-${index}`;
  return firstNonEmptyString([candidate, label, `slot-${index}`]);
}

function resolveLegacySlotValue(
  dto: AppointmentSlotTimeDto,
  label: string,
  startTime: string,
): string {
  const fromValue = toTrimmedString(dto.value);
  if (fromValue.length > 0) {
    return fromValue;
  }
  if (startTime.length > 0) {
    return startTime;
  }
  return label;
}

function mapLegacySlotDto(
  dto: AppointmentSlotTimeDto,
  index: number,
): MappedSlotTime | null {
  const startTime = toTrimmedString(dto.start_time);
  const labelFromStart = startTime ? formatSlotStartLabel(startTime) : '';
  const label = firstNonEmptyString([
    dto.label,
    labelFromStart,
    dto.time,
    dto.slot_time,
    dto.value,
  ]);
  if (!label) {
    return null;
  }

  return {
    id: resolveLegacySlotId(dto, label, index),
    label,
    value: resolveLegacySlotValue(dto, label, startTime),
    ...(dto.disabled === true || isSlotStatusUnavailable(dto.status)
      ? { disabled: true }
      : {}),
  };
}

function mapPrimarySlotDetail(
  slot: AppointmentSlotDetail,
  index: number,
): MappedSlotTime | null {
  const startTime = String(slot.start_time ?? '').trim();
  const label = formatSlotStartLabel(startTime);
  if (!label) {
    return null;
  }
  return {
    id: String(slot.appointment_slot_id ?? startTime ?? `slot-${index}`),
    label,
    value: startTime || label,
    ...(isSlotStatusUnavailable(slot.status) ? { disabled: true } : {}),
  };
}

/**
 * Map slots API payload → chip list.
 * Primary shape: `{ details: [{ appointment_slot_id, start_time, ... }] }`
 */
export function mapSlotTimesFromResponse(
  response: AppointmentSlotsResponse | null | undefined,
): MappedSlotTime[] {
  const details = extractSlotDetails(response);
  const mapped =
    details.length > 0
      ? details
          .map((slot, index) => mapPrimarySlotDetail(slot, index))
          .filter((slot): slot is MappedSlotTime => slot != null)
      : extractLegacySlotTimeDtos(response)
          .map((dto, index) => mapLegacySlotDto(dto, index))
          .filter((slot): slot is MappedSlotTime => slot != null);

  return [...mapped].sort(compareMappedSlotTimes);
}

/**
 * Pull the soonest upcoming appointment from API response.
 * Primary shape: `{ details: [{ appointment_id, start_time, sales_agent_name, ... }] }`
 */
export function extractUpcomingAppointment(
  response: UpcomingAppointmentResponse | null | undefined,
): UpcomingAppointmentDto | null {
  if (!response) {
    return null;
  }

  const details = extractUpcomingDetails(response);
  if (details.length > 0) {
    const scheduled = details.filter((item) => {
      const status = String(item.appointment_status ?? '').trim().toLowerCase();
      // Keep active bookings only (exclude Cancelled / completed).
      return !status || status === 'scheduled' || status === 'rescheduled';
    });
    const pool = scheduled.length > 0 ? scheduled : details;
    const sorted = [...pool].sort((a, b) => {
      const aMs = new Date(String(a.start_time ?? '')).getTime();
      const bMs = new Date(String(b.start_time ?? '')).getTime();
      const safeA = Number.isNaN(aMs) ? Number.POSITIVE_INFINITY : aMs;
      const safeB = Number.isNaN(bMs) ? Number.POSITIVE_INFINITY : bMs;
      return safeA - safeB;
    });
    const soonest = sorted[0];
    return soonest ? (soonest as UpcomingAppointmentDto) : null;
  }

  const candidate = response.appointment ?? response.data ?? null;
  const record = asRecord(candidate);
  if (!record) {
    return null;
  }
  const hasSignal = Boolean(
    record.start_time ||
      record.date ||
      record.appointment_date ||
      record.time ||
      record.appointment_time ||
      record.date_id ||
      record.time_id ||
      record.id ||
      record.appointment_id ||
      record.event_id,
  );
  return hasSignal ? (record as UpcomingAppointmentDto) : null;
}

function extractUpcomingDetails(
  response: UpcomingAppointmentResponse,
): UpcomingAppointmentDetail[] {
  if (Array.isArray(response.details)) {
    return response.details;
  }
  if (Array.isArray(response.data)) {
    return response.data as UpcomingAppointmentDetail[];
  }
  const data = asRecord(response.data);
  if (data && Array.isArray(data.details)) {
    return data.details as UpcomingAppointmentDetail[];
  }
  return [];
}

/** YYYY-MM-DD in UAE for a slot/appointment ISO timestamp. */
export function toAppointmentDateId(isoStart: string): string {
  const date = new Date(isoStart);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
}

/** Chip/display time label e.g. "9:00 PM" (UAE). */
export function formatAppointmentTimeLabel(isoStart: string): string {
  return formatSlotStartLabel(isoStart);
}

function readIdField(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  const raw = record.appointment_id ?? record.id;
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return null;
  }
  const id = String(raw).trim();
  return id.length > 0 ? id : null;
}

function readEventIdField(
  record: Record<string, unknown> | null,
): string | null {
  if (!record) {
    return null;
  }
  const raw = record.event_id;
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return null;
  }
  const id = String(raw).trim();
  return id.length > 0 ? id : null;
}

/**
 * Read appointment_id from schedule/update payloads.
 * BE often returns `details` as a single object (not an array).
 */
export function extractAppointmentIdFromMutationResponse(
  response: unknown,
): string | null {
  const root = asRecord(response);
  if (!root) {
    return null;
  }

  const fromRoot = readIdField(root);
  if (fromRoot) {
    return fromRoot;
  }

  const details = root.details;
  if (Array.isArray(details) && details.length > 0) {
    return readIdField(asRecord(details[0]));
  }

  const fromDetails = readIdField(asRecord(details));
  if (fromDetails) {
    return fromDetails;
  }

  const fromData = readIdField(asRecord(root.data));
  if (fromData) {
    return fromData;
  }

  return readIdField(asRecord(root.appointment));
}

/**
 * Read event_id from schedule/update/upcoming payloads.
 * Cancel & reschedule PATCH must send this (not appointment_id).
 */
export function extractEventIdFromMutationResponse(
  response: unknown,
): string | null {
  const root = asRecord(response);
  if (!root) {
    return null;
  }

  const fromRoot = readEventIdField(root);
  if (fromRoot) {
    return fromRoot;
  }

  const details = root.details;
  if (Array.isArray(details) && details.length > 0) {
    return readEventIdField(asRecord(details[0]));
  }

  const fromDetails = readEventIdField(asRecord(details));
  if (fromDetails) {
    return fromDetails;
  }

  const fromData = readEventIdField(asRecord(root.data));
  if (fromData) {
    return fromData;
  }

  return readEventIdField(asRecord(root.appointment));
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Prefer string/number fields; avoid String(object) → "[object Object]". */
function readApiTextField(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return fallback;
}

/**
 * Many appointment APIs return HTTP 200 with `{ status: "error", message }`.
 * Only treat explicit success as OK so UI does not show success dialogs on failure.
 */
export function assertAppointmentApiSuccess(
  response: unknown,
  operation: string,
): void {
  const root = asRecord(response);
  const status = readApiTextField(root?.status)
    .trim()
    .toLowerCase();
  if (status === 'success') {
    return;
  }
  const message = readApiTextField(
    root?.message ?? root?.error,
    `${operation} failed`,
  ).trim();
  throw new Error(message || `${operation} failed`);
}

/**
 * Shared appointments API (web + native).
 * Bearer JWT via apiClient — customer resolved from login token.
 */
export class AppointmentService {
  async getSlots(
    params: GetAppointmentSlotsParams = {},
  ): Promise<AppointmentSlotsResponse> {
    const query = {
      /** Book: today. Reschedule: pass the scheduled appointment date from the caller. */
      date: params.date ?? toIsoDate(new Date()),
      ...(params.unitId ? { unit_id: params.unitId } : {}),
    };

    logger.info('AppointmentService:Fetching slots', query);

    return apiClient.get<AppointmentSlotsResponse>(
      Endpoints.appointment.slots,
      { params: query },
    );
  }

  async getUpcoming(
    _params: GetUpcomingAppointmentParams = {},
  ): Promise<UpcomingAppointmentResponse> {
    logger.info('AppointmentService:Fetching upcoming appointment');

    return apiClient.get<UpcomingAppointmentResponse>(Endpoints.appointment.upcoming);
  }

  async scheduleAppointment(
    body: Partial<ScheduleAppointmentRequest> & {
      appointment_slot_id: string;
      unitId?: string;
    },
  ): Promise<ScheduleAppointmentResponse> {
    const unitId = body.unit_id ?? body.unitId;
    const payload: ScheduleAppointmentRequest = {
      appointment_slot_id: body.appointment_slot_id,
      ...(unitId ? { unit_id: unitId } : {}),
    };

    logger.info('AppointmentService:Scheduling appointment', payload);

    const response = await apiClient.post<ScheduleAppointmentResponse>(
      Endpoints.appointment.schedule,
      payload,
    );
    assertAppointmentApiSuccess(response, 'Schedule appointment');
    return response;
  }

  async updateAppointment(
    body: Partial<UpdateAppointmentRequest> & {
      action_type: UpdateAppointmentRequest['action_type'];
      event_id: string;
    },
  ): Promise<UpdateAppointmentResponse> {
    const payload: UpdateAppointmentRequest = {
      action_type: body.action_type,
      event_id: body.event_id,
      reason: body.reason ?? '',
      appointment_slot_id: body.appointment_slot_id ?? '',
    };

    logger.info('AppointmentService:Updating appointment', payload);

    const response = await apiClient.patch<UpdateAppointmentResponse>(
      Endpoints.appointment.update,
      payload,
    );
    assertAppointmentApiSuccess(response, 'Update appointment');
    return response;
  }
}

export const appointmentService = new AppointmentService();
